import { Error as ErrorIcon, RedoOutlined as RedoIcon } from "@mui/icons-material";
import { Button, GridLegacy as Grid, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { compact } from "lodash-es";
import { useCallback, useEffect, useState } from "react";

import { SENSITIVE_BY_CLASS_NAME } from "../../utils/highlightUtils";

interface IProps {
    isChallengeInitializing: boolean;
    isChallengeInitialized: boolean;
    isSubmitting: boolean;
    isCodeInvalid: boolean;
    codeLength?: number;

    renderBody?(): React.ReactNode;
    renderError?(): React.ReactNode;

    onReinitializeChallenge(): void;
    onSubmit(verificationCode: string): void;
}

export const MfaChallenge: React.FC<IProps> = ({
    isChallengeInitializing,
    isChallengeInitialized,
    isSubmitting,
    isCodeInvalid,
    codeLength,
    renderBody,
    renderError,
    onReinitializeChallenge,
    onSubmit,
}) => {
    const [verificationCode, setVerificationCode] = useState("");

    useEffect(
        () => {
            if (codeLength != null && verificationCode.length === codeLength) {
                // If codeLength provided, automatically submit when code reaches length.
                onSubmit(verificationCode);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [verificationCode, codeLength],
    );

    const handleChangeInput = useCallback(
        ({ target: { value } }: React.ChangeEvent<HTMLInputElement>): void =>
            setVerificationCode(value.replace(/[^\d]/g, "").slice(0, codeLength)),
        [codeLength],
    );

    const handleClickSubmit = useCallback((): void => onSubmit(verificationCode), [verificationCode, onSubmit]);

    return (
        <Stack spacing={3} maxWidth={600}>
            <Typography variant="h1">
                {compact(["Enter", codeLength != null && `${codeLength}-digit`, "code"]).join(" ")}
            </Typography>
            {renderBody?.()}
            <Stack spacing={2}>
                <TextField
                    autoComplete="one-time-code"
                    fullWidth={true}
                    autoFocus={true}
                    error={isCodeInvalid}
                    value={verificationCode}
                    InputProps={{
                        endAdornment: isCodeInvalid && (
                            <Tooltip title="Invalid confirmation code" placement="top-end">
                                <ErrorIcon color="error" />
                            </Tooltip>
                        ),
                    }}
                    inputProps={SENSITIVE_BY_CLASS_NAME}
                    onChange={handleChangeInput}
                />
                <div>
                    <Grid container={true} spacing={2} minWidth={300}>
                        <Grid item={true} xs={6}>
                            <Button
                                variant="outlined"
                                fullWidth={true}
                                loading={isChallengeInitializing}
                                startIcon={<RedoIcon />}
                                onClick={onReinitializeChallenge}
                            >
                                Resend
                            </Button>
                        </Grid>
                        <Grid item={true} xs={6}>
                            <Button
                                variant="contained"
                                type="submit"
                                fullWidth={true}
                                loading={isSubmitting}
                                disabled={
                                    isChallengeInitializing ||
                                    !isChallengeInitialized ||
                                    (codeLength != null && verificationCode.length !== codeLength)
                                }
                                onClick={handleClickSubmit}
                            >
                                Submit
                            </Button>
                        </Grid>
                    </Grid>
                </div>
                {renderError?.()}
            </Stack>
        </Stack>
    );
};
